// Liste explicative d'une tuile : mêmes filtres que la tuile, nombre de lignes égal à la valeur
// affichée (R16). Pour un indicateur, le nombre est le total compté par le serveur, la liste montre
// jusqu'à 1 000 lignes et l'export CSV vient du serveur (même requête de base, toutes les lignes) ;
// pour une tuile du socle, la liste et son export sont ceux du navigateur.
import { Bandeau, Bouton, EtatVide, LienBouton, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { fraicheur } from '../../commun/format';
import { Lien, Squelette } from '@cc/ui';
import { tuile } from './catalogue';
import { parametresIndicateur, useListeIndicateur, useListeSocle, useModulesActifs, type FiltresTableau, type LigneExplicative } from './donnees';
import { calculerPeriode, type CodePeriode } from './periode';
import { telechargerBlob, telechargerCsv } from '../metier/csv';

const COLONNES: ColonneTableau<LigneExplicative>[] = [
  {
    id: 'reference',
    entete: 'Référence',
    enTeteLigne: true,
    masquable: false,
    valeurTri: (l) => l.reference,
    cellule: (l) => (l.href ? <Lien href={l.href}>{l.reference}</Lien> : l.reference),
  },
  { id: 'objet', entete: 'Objet', cellule: (l) => l.objet, valeurTri: (l) => l.objet },
  { id: 'detail', entete: 'Précision', cellule: (l) => l.detail ?? 'non renseigné' },
  { id: 'etat', entete: 'État', cellule: (l) => l.etat ?? 'non renseigné', valeurTri: (l) => l.etat ?? null },
];

export function PageListeIndicateur() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const definition = tuile(code ?? '');
  const filtres: FiltresTableau = { periode: (params.get('periode') as CodePeriode) ?? 'mois', arrondissement: params.get('arrondissement') ?? '' };
  const modulesActifs = useModulesActifs();
  const moduleInactif = Boolean(definition?.module && modulesActifs && !modulesActifs.has(definition.module));
  const indicateur = useListeIndicateur(definition, filtres, Boolean(definition) && !moduleInactif);
  const socle = useListeSocle(definition?.code, definition?.source === 'socle');
  const [exportEnCours, setExportEnCours] = useState(false);
  const [erreurExport, setErreurExport] = useState<string | null>(null);

  if (!definition) {
    return (
      <EtatVide titre="Indicateur inconnu" niveau={2} action={<LienBouton href="/">Revenir au tableau de bord</LienBouton>}>
        <p>Le code {code} ne figure pas au catalogue des tuiles.</p>
      </EtatVide>
    );
  }
  const periode = calculerPeriode(filtres.periode);
  const lignes = definition.source === 'socle' ? socle.lignes : (indicateur.data?.lignes ?? null);
  const total = definition.source === 'socle' ? (lignes?.length ?? 0) : (indicateur.data?.total ?? 0);
  const chargement = definition.source === 'socle' ? socle.chargement : indicateur.isPending && !moduleInactif;
  const erreur = definition.source === 'socle' ? socle.erreur : indicateur.error;
  const nomExport = `${definition.code}-${periode.debut}-${periode.fin}.csv`;

  const exporter = async () => {
    if (!lignes) return;
    if (definition.source === 'socle') {
      telechargerCsv(nomExport, ['Référence', 'Objet', 'Précision', 'État'], lignes.map((l) => [l.reference, l.objet, l.detail ?? '', l.etat ?? '']));
      return;
    }
    setErreurExport(null);
    setExportEnCours(true);
    try {
      const r = await requete<Response>('/indicateurs/export', { brut: true, parametres: { ...parametresIndicateur(definition.code, filtres), format: 'csv' } });
      telechargerBlob(nomExport, await r.blob());
    } catch (e) {
      setErreurExport(messageErreur(e));
    } finally {
      setExportEnCours(false);
    }
  };

  return (
    <>
      <EnTetePage
        etiquette={`Indicateur ${definition.code}`}
        titre={definition.libelle}
        description={
          <>
            {definition.description}.{' '}
            {definition.instantane ? 'Situation à date.' : `Période : ${periode.libelle} (du ${periode.debut} au ${periode.fin}).`}
            {filtres.arrondissement ? ' Filtré sur un arrondissement.' : ''}
            {indicateur.data?.calcule_le ? ` Calculé ${fraicheur(indicateur.data.calcule_le)}.` : ''}
          </>
        }
        actions={
          <>
            <LienBouton href="/" icone={ArrowLeft}>
              Tableau de bord
            </LienBouton>
            {lignes && lignes.length > 0 ? (
              <Bouton variante="secondaire" icone={Download} chargement={exportEnCours} libelleChargement="Export en cours" onClick={() => void exporter()}>
                Exporter en CSV
              </Bouton>
            ) : null}
          </>
        }
      />
      {erreurExport ? <Bandeau type="erreur">{erreurExport}</Bandeau> : null}
      {moduleInactif || indicateur.data?.couverture === 'non_active' ? (
        <EtatVide titre="Module non activé">
          <p>Le module {definition.module} est désactivé : l&apos;indicateur n&apos;est pas calculé.</p>
        </EtatVide>
      ) : chargement ? (
        <Squelette lignes={6} />
      ) : erreur ? (
        <AffichageErreur erreur={erreur} lot="L0-C" fonction="La liste explicative de cet indicateur" />
      ) : lignes && lignes.length === 0 ? (
        <EtatVide titre="Aucune ligne">
          <p>Aucun élément ne correspond à cet indicateur pour ces filtres.</p>
        </EtatVide>
      ) : lignes ? (
        <>
          <p role="status" data-testid="nombre-lignes">
            {total.toLocaleString('fr-FR')} ligne{total > 1 ? 's' : ''}
          </p>
          {lignes.length < total ? (
            <p>
              Les {lignes.length.toLocaleString('fr-FR')} premières lignes sont affichées ; l&apos;export CSV les contient toutes.
            </p>
          ) : null}
          <Tableau legende={`Liste explicative : ${definition.libelle}`} colonnes={COLONNES} lignes={lignes} cleLigne={(l) => l.id} />
        </>
      ) : null}
    </>
  );
}
